import React, { forwardRef, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  // Table,
  Pagination,
  Select,
  Checkbox,
  Input,
  ToggleVisibility,
  Button,
} from "components/ui";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/Table";
import TableRowSkeleton from "./loaders/TableRowSkeleton";
import Loading from "./Loading";
import {
  useTable,
  usePagination,
  useSortBy,
  useRowSelect,
  useFilters,
} from "react-table";
import cn from "classnames";
import { useState } from "react";
import { HiOutlineSearch } from "react-icons/hi";
import { AiOutlineCloudDownload } from "react-icons/ai";
import { useEditTableCell } from "utils/hooks/useEditTableCell";
import { useOnClickOutside } from "usehooks-ts";
import { useMenu } from "utils/hooks/useMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "components/ui/Dropdown";
import { FiMoreHorizontal } from "react-icons/fi";

const CustomCell = ({ column, row, value, ...data }) => {
  return (
    <span>
      <>
        {column?.editable && data?.isEditId === row?.id ? (
          <Input {...row.getEditRowCellProps(column?.id)} name={column?.id} />
        ) : (
          <div>{value}</div>
        )}
      </>
    </span>
  );
};

// const { Tr, Th, Td, THead, TBody, Sorter } = Table;

const IndeterminateCheckbox = forwardRef((props, ref) => {
  const {
    indeterminate,
    onChange,
    onCheckBoxChange,
    onIndeterminateCheckBoxChange,
    ...rest
  } = props;

  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  const handleChange = (e) => {
    onChange(e);
    onCheckBoxChange?.(e);
    onIndeterminateCheckBoxChange?.(e);
  };

  return (
    <Checkbox
      className="mb-0"
      ref={resolvedRef}
      onChange={(_, e) => handleChange(e)}
      {...rest}
    />
  );
});

const DataTable = forwardRef((props, ref) => {
  const {
    skeletonAvatarColumns,
    columns,
    data,
    loading,
    onCheckBoxChange,
    onIndeterminateCheckBoxChange,
    onPaginationChange,
    onSelectChange,
    onSort,
    selectable,
    skeletonAvatarProps,
    pagingData,
    useBlockFooter,
    onSubmit,
    options,
  } = props;
  const { pageSize, pageIndex, total } = pagingData;
  const menuRef = useRef();
  const tableRef = useRef();
  const triggerRefs = useRef([]);
  const [activeMenu, setActiveMenu] = useState(null);

  const {
    disablePageSize,
    manualPagination,
    showHeader,
    pageSizes,
    searchable,
    editable,
    filterTerm,
    disableCsvDownload,
    enableMenu,
    serverSearch,
    onSearch,
    isOverflow,
  } = options;

  let counts = useMemo(
    () => (manualPagination ? pageSize : Math.ceil((data.length - 1) / 10)),
    [pageSize, manualPagination, data?.length]
  );


  const [totalPages, setTotalPages] = useState(() => counts);
  const [filterInput, setFilterInput] = useState("");



  const pageSizeOption = useMemo(
    () =>
      pageSizes?.map?.((number) => ({
        value: number,
        label: `${number} / page`,
      })),
    [pageSizes]
  );

  const handleCheckBoxChange = (checked, row) => {
    if (!loading) {
      onCheckBoxChange?.(checked, row);
    }
  };

  const handleIndeterminateCheckBoxChange = (checked, rows) => {
    if (!loading) {
      onIndeterminateCheckBoxChange?.(checked, rows);
    }
  };

  useOnClickOutside(menuRef, (event) => {
    if (activeMenu !== null && event?.target.id !== "menu") {
      setActiveMenu(null);
    }
  });

  // console.log(totalPages);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setPageSize,
    formValues,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state,
    setFilter,
  } = useTable(
    {
      columns,
      data,
      manualPagination,
      manualSortBy: true,
      enableEditing: editable,
      autoResetSelectedCell: false,
      autoResetSelectedRows: false,
      autoResetSelectedColumn: false,
      initialState: { pageIndex: 0, enableMenu },
      defaultColumn: editable ? { Cell: CustomCell } : undefined,
    },
    useFilters,
    useMenu,
    useSortBy,
    usePagination,
    useRowSelect,
    useEditTableCell,
    (hooks) => {
      if (enableMenu) {
        // console.log(hooks);
        hooks.visibleColumns.push((columns) => [
          ...columns?.filter((col) => col.id !== "actions"),
          {
            Header: "Actions",
            id: "actions",
            Cell: ({
              row: { original, index, ...values },
              dispatch,
              ...val
            }) => {
              // console.log("row", val);
              const actionData = columns.find((col) => col.id === "actions");

              return (
                <div id="menu">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="p-0 bg-green-700">
                        <span className="sr-only">Open menu</span>
                        <FiMoreHorizontal className="h-4 w-4" />
                      </div>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      align="end"
                      side="left"
                      className="bg-white"
                    >
                      {actionData?.getMenuOptions?.(original)?.map?.(
                        (item, index) =>
                          item?.keys?.includes(
                            item?.selector?.toString()?.toLowerCase?.()
                          ) && (
                            <DropdownMenuItem
                              className="hover:bg-secondary gap-3 hover:text-white"
                              key={index}
                              onClick={() => {
                                item?.change?.(original?.id, original);
                                if (item.delay) {
                                  setTimeout(() => {}, 60000);
                                }
                                dispatch({
                                  type: "toggleMenuVisibility",
                                  payload: {
                                    activeMenu: null,
                                    isActiveMenu: false,
                                  },
                                });
                              }}
                            >
                              {item?.loading ? (
                                <Loading spinnerClass="!w-5 !h-5" loading />
                              ) : null}

                              {item?.icon}

                              {item?.title}
                            </DropdownMenuItem>
                          )
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            },
          },
        ]);
      }

      if (selectable) {
        hooks.visibleColumns.push((columns) => [
          {
            id: "selection",
            Header: (props) => (
              <div>
                <IndeterminateCheckbox
                  {...props.getToggleAllRowsSelectedProps()}
                  onIndeterminateCheckBoxChange={(e) =>
                    handleIndeterminateCheckBoxChange(
                      e.target.checked,
                      props.rows
                    )
                  }
                />
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox
                  {...row.getToggleRowSelectedProps()}
                  onCheckBoxChange={(e) =>
                    handleCheckBoxChange(e.target.checked, row.original)
                  }
                />
              </div>
            ),
            sortable: false,
          },
          ...columns,
        ]);
      }
    }
  );

  // Update the state when input changes
  const handleFilterChange = (e) => {
    const value = e.target.value || undefined;
    if (!serverSearch) {
      setFilter(filterTerm, value);
      setFilterInput(value);
      return;
    }

    setFilterInput(value);
    onSearch(value);
  };

  const handlePaginationChange = (page) => {
    if (!loading) {
      gotoPage(page - 1);
      onPaginationChange?.(page);
    }
  };

  const handleSelectChange = (value) => {
    if (!loading) {
      setPageSize(Number(value));
      onSelectChange?.(Number(value));
      setTotalPages((data.length - 1) / Number(value));
    }
  };

  const handleSort = (column) => {
    if (!loading) {
      const { id, isSortedDesc, toggleSortBy, clearSortBy } = column;
      const sortOrder = isSortedDesc ? "desc" : "asc";
      toggleSortBy(!isSortedDesc);
      onSort?.({ order: sortOrder, key: id }, { id, clearSortBy });
    }
  };

  function downloadCSV(filename) {
    // Collect the data from the table
    const csvContent = [
      columns.map((column) => column.Header).join(","),
      ...page.map((row) => row.cells.map((cell) => cell.value).join(",")),
    ].join("\n");

    // Create a blob object with the CSV data
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Create a download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;

    // Append the link to the document and click it programmatically to start the download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }


  return (
    <Loading loading={loading && data.length !== 0} type="cover">
      {showHeader && (
        <div
          className={cn(
            "bg-slate-100 px-2 py-1 flex items-center justify-between"
            // { overflow: page?.length < 4 }
          )}
        >
          <div className="flex-1">
            {searchable && (
              <div className="relative flex">
                <Input
                  // {...input}
                  value={filterInput}
                  onChange={handleFilterChange}
                  type="text"
                  // placeholder={placeholder}
                  className="border rounded-lg rounded--none px-8 p-2 !w-64 text-base shad"
                />
                <HiOutlineSearch className="absolute top-1/2 left-3 -translate-y-1/2" />
              </div>
            )}
          </div>
          <div className="flex flex-1 items-center justify-end pr-4">
            {!disableCsvDownload && (
              <AiOutlineCloudDownload
                onClick={() => downloadCSV("table-data")}
                className="h-5 w-5 cursor-pointer "
              />
            )}
          </div>
        </div>
      )}
      <div>
        <Table
          overflow={page?.length > 4}
          ref={tableRef}
          {...getTableProps()}
          className="min-w-full overflow-x-auto"
        >
          <TableHeader>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <TableHead {...column.getHeaderProps()}>
                    {column.render("Header") &&
                      (column.sortable ? (
                        <div
                          className="cursor-pointer"
                          onClick={() => handleSort(column)}
                        >
                          {column.render("Header")}
                          <span>
                            {/* <Sorter sort={column.isSortedDesc} /> */}
                          </span>
                        </div>
                      ) : (
                        <div>{column.render("Header")}</div>
                      ))}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          {loading && data?.length === 0 ? (
            <TableRowSkeleton
              avatarInColumns={skeletonAvatarColumns}
              avatarProps={skeletonAvatarProps}
            />
          ) : (
            <TableBody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <TableCell {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </div>


      <ToggleVisibility
        as="div"
        isVisible={!loading && totalPages > 1}
        className={cn("grid gap-2 mt-4 px-2", {
          "grid-cols-2": !useBlockFooter,
        })}
      >
        <Pagination
          total={totalPages}
          // pageSize={pageSize}
          currentPage={state.pageIndex + 1}
          onChange={handlePaginationChange}
        />

        {!disablePageSize && (
          <div style={{ minWidth: 130 }}>
            <Select
              size="sm"
              menuPlacement="top"
              isSearchable={false}
              options={pageSizeOption}
              onChange={(option) => handleSelectChange(option.value)}
              value={pageSizeOption?.filter?.(
                (option) => option.value === state.pageSize
              )}
            />
          </div>
        )}
      </ToggleVisibility>
    </Loading>
  );
});

DataTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  loading: PropTypes.bool,
  onCheckBoxChange: PropTypes.func,
  onIndeterminateCheckBoxChange: PropTypes.func,
  onPaginationChange: PropTypes.func,
  onSelectChange: PropTypes.func,
  onSort: PropTypes.func,
  selectable: PropTypes.bool,
  skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
  skeletonAvatarProps: PropTypes.object,
  onSubmit: PropTypes.func,
  options: PropTypes.shape({
    manualPagination: PropTypes.bool,
    filterTerm: PropTypes.string,
    showHeader: PropTypes.bool,
    pageSizes: PropTypes.arrayOf(PropTypes.number),
    searchable: PropTypes.bool,
    disablePageSize: PropTypes.bool,
    disableCsvDownload: PropTypes.bool,
    editable: PropTypes.bool,
  }),
  pagingData: PropTypes.shape({
    total: PropTypes.number,
    pageIndex: PropTypes.number,
    pageSize: PropTypes.number,
  }),
  useBlockFooter: PropTypes.bool,
};

DataTable.defaultProps = {
  pagingData: {
    total: 0,
    pageIndex: 1,
    pageSize: 10,
  },
  options: {
    filterTerm: "name",
    showHeader: false,
    searchable: false,
    disablePageSize: false,
    disableCsvDownload: false,
    editable: false,
    pageSizes: [10, 25, 50, 100],
  },
  data: [],
  columns: [],
  selectable: false,
  loading: false,
  useBlockFooter: false,
};

export default DataTable;

// ==========================================================================================
